'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { Navigation, Pagination, Keyboard, A11y } from 'swiper/modules';
import 'swiper/swiper-bundle.css';
import './styles.css';
import { useIsMobile } from 'WindowDimensionProvider';
import { BrandFlagContainer } from 'components/molecules/BrandFlagContainer';
import { AVAILABLE_BRAND_FEATURES } from '@kouto/types';
import { isFeatureActive } from 'utils/featureFlag';
import { listingMediaIsVideo } from 'utils/listings';
import { FormattedListingMedia } from 'types/listings';
import styled from 'styled-components';
import MinimalVideoPlayer from 'components/MinimalVideoPlayer/MinimalVideoPlayer';
import { useTranslation } from 'react-i18next';

const StyledMinimalVideoPlayer = styled(MinimalVideoPlayer)<{
  isFullScreen?: boolean;
}>`
  & > .play-pause-button,
  & > .mute-button {
    ${({ isFullScreen }) =>
      isFullScreen &&
      `
        background-color: rgba(0, 0, 0, 0.5);
        color: white;
      `}
  }
`;

interface SwiperGalleryProps {
  medias: (FormattedListingMedia & {
    alt?: string;
  })[];
  brandFlagName?: string | null;
}

const MediaCarousel = ({ medias, brandFlagName }: SwiperGalleryProps) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [activeRealIndex, setActiveRealIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const swiperRef = useRef<SwiperType | null>(null);
  const isRequestToBookEnabled = isFeatureActive(
    AVAILABLE_BRAND_FEATURES.ENABLE_REQUEST_BOOKINGS,
  );
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  // Handle slide change to track current index
  const handleSlideChange = (swiper: SwiperType) => {
    setActiveSlideIndex(swiper.activeIndex);
    setActiveRealIndex(swiper.realIndex);
  };

  // Handle fullscreen toggle with proper slide index and autoplay management
  const toggleFullscreen = () => {
    if (!isRequestToBookEnabled) return;

    if (!isFullscreen) {
      // Prevent body scrolling
      document.body.style.overflow = 'hidden';

      setIsFullscreen(true);
      // Move focus to the fullscreen toggle for accessibility
      setTimeout(() => containerRef.current?.focus(), 0);
    } else {
      // Exiting fullscreen

      // Restore body scrolling
      document.body.style.overflow = '';

      setIsFullscreen(false);
      if (swiperRef.current) {
        // Remove explicit width styles from all slides
        for (let i = 0; i < swiperRef.current.slides.length; i += 1) {
          (swiperRef.current.slides[i] as HTMLElement).style.width = '';
        }

        // Update Swiper
        swiperRef.current.update();

        // Restore slide index
        setTimeout(() => {
          if (swiperRef.current) {
            swiperRef.current.slideTo(activeSlideIndex);
          }
        }, 50);
      }
    }
  };

  const handleSwiperInit = (swiper: SwiperType) => {
    swiperRef.current = swiper;
    swiper.slideToLoop(0, 0);
  };

  useEffect(() => {
    return () => {
      // Cleanup: restore scrolling if component unmounts while in fullscreen
      if (isFullscreen) {
        document.body.style.overflow = '';
      }
    };
  }, [isFullscreen]);

  // Add this useEffect for global escape key handling
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullscreen) {
        toggleFullscreen();
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isFullscreen]);

  return (
    <div
      ref={containerRef}
      className="media-carousel-container"
      role="region"
      aria-label={t('mediaGallery')}
      style={{
        position: isFullscreen ? 'absolute' : 'relative',
        width: '100%',
        height: isFullscreen ? '100dvh' : '100%',
        background: '#000',
        zIndex: isFullscreen ? 9999 : 'auto',
        top: isFullscreen ? '0' : 'auto',
        left: isFullscreen ? '0' : 'auto',
      }}
    >
      {isFullscreen && (
        <button
          type="button"
          onClick={toggleFullscreen}
          className="fullscreen-close-button"
          aria-label={t('closeFullscreen') || 'Close fullscreen'}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
          }}
        >
          ×
        </button>
      )}
      <BrandFlagContainer brandFlagName={brandFlagName}>
        <Swiper
          className="carousel-slider"
          modules={[Navigation, Pagination, Keyboard, A11y]}
          onSwiper={handleSwiperInit}
          onSlideChange={handleSlideChange}
          pagination={{
            clickable: true,
            dynamicBullets: true,
          }}
          navigation={medias?.length > 1}
          keyboard={{ enabled: true }}
          loop
          initialSlide={0}
          spaceBetween={20}
          // eslint-disable-next-line no-nested-ternary
          slidesPerView={isFullscreen ? 1 : 'auto'}
          centeredSlides={isFullscreen}
          a11y={{
            enabled: true,
            prevSlideMessage: t('previousSlide'),
            nextSlideMessage: t('nextSlide'),
            paginationBulletMessage: `${t(
              'paginationBulletMessage',
            )} {{index}} ${t('of')} ${medias.length}`,
            slideLabelMessage: '',
          }}
          style={{
            width: '100%',
            height: '100%',
          }}
        >
          {medias.map((imageObject, index) => {
            const uniqueKey = imageObject.urlDesktop.includes('/')
              ? imageObject.urlDesktop.split('/').pop()
              : imageObject.urlDesktop;
            const isVideo = listingMediaIsVideo(imageObject);
            const isVideoActive = swiperRef.current?.realIndex === index;

            return (
              <SwiperSlide
                key={`slide-${uniqueKey}`}
                aria-label={
                  isVideo
                    ? `${t('videoSlide')} ${index + 1} ${t('of')} ${
                        medias.length
                      }. ${imageObject.alt || t('noDescription')}`
                    : `${t('imageSlide')} ${index + 1} ${t('of')} ${
                        medias.length
                      }. ${imageObject.alt || t('noDescription')}`
                }
                role="button"
                tabIndex={index === activeRealIndex ? 0 : -1}
                {...(index !== activeRealIndex ? { inert: true } : {})}
                onClick={() => {
                  if (!isFullscreen) {
                    toggleFullscreen();
                  }
                }}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && !isFullscreen) {
                    e.preventDefault();
                    toggleFullscreen();
                  } else if (e.key === 'Escape' && isFullscreen) {
                    e.preventDefault();
                    toggleFullscreen();
                  }
                }}
                style={{
                  display: 'flex',
                  justifyContent:
                    isFullscreen || isMobile ? 'center' : 'flex-start',
                  alignItems: 'center',
                  cursor: 'pointer',
                  flexShrink: 0,
                  // eslint-disable-next-line no-nested-ternary
                  width: isFullscreen ? '100vw' : 'auto',
                  maxWidth: '100%',
                }}
              >
                {isVideo ? (
                  <StyledMinimalVideoPlayer
                    isFullScreen={isFullscreen}
                    key={imageObject.id}
                    fit="cover"
                    videoUrl={
                      isMobile ? imageObject.urlMobile : imageObject.urlDesktop
                    }
                    coverUrl={
                      isMobile
                        ? imageObject.previewMobile
                        : imageObject.previewDesktop
                    }
                    play={isVideoActive}
                    style={{
                      cursor: isRequestToBookEnabled ? 'pointer' : 'default',
                      height: isFullscreen ? '75vh' : '100%',
                      width: '100%',
                    }}
                  />
                ) : (
                  <img
                    src={
                      isMobile ? imageObject.urlMobile : imageObject.urlDesktop
                    }
                    alt={imageObject.alt || t('noDescription')}
                    style={{
                      width: index === 0 && !isFullscreen ? '100%' : 'auto',
                      maxWidth: '100%',
                      height:
                        // eslint-disable-next-line no-nested-ternary
                        index === 0 && !isFullscreen
                          ? 'auto'
                          : isFullscreen
                          ? '80vh'
                          : '100%',
                      objectFit: 'contain',
                    }}
                  />
                )}
              </SwiperSlide>
            );
          })}
        </Swiper>
      </BrandFlagContainer>
    </div>
  );
};

export default MediaCarousel;
